import { compact } from "lodash";
import AppContext from "PFApp/app_context";
import { ActionIcon } from "PFComponents/action_icon";
import { RadialBar } from "PFComponents/radial_bar/radial_bar";
import { getActivitySkills } from "PFCore/helpers/activity_skills";
import { divideArray } from "PFCore/helpers/divide_array";
import { getMatchDisplayValue } from "PFCore/helpers/match_value";
import { Id, MatchedActivity, PureCustomValue } from "PFTypes";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";

import { useMarketplaceContext } from "../../../../../contexts/marketplace_context";
import { Section } from "../../components";
import { MarketplaceSkillBadge } from "./components/marketplace_skill_badge";
import css from "./skills_section.module.scss";

type SkillsSectionProps = {
  role: MatchedActivity["activity"];
};

export const SkillsSection = ({ role }: SkillsSectionProps) => {
  const {
    store: { currentProfile }
  } = useContext(AppContext);
  const { t } = useTranslation("marketplace", { keyPrefix: "workOpportunities.roleDetails.skills" });
  const { modal, selectedRoleScores } = useMarketplaceContext();

  const profileSkills = currentProfile.skills || [];
  const roleSkills = getActivitySkills(role);

  const getProfileSkill = (id: Id) => profileSkills?.find((profileSkill) => id === profileSkill.id);

  const [matchingSkills, unmatchedSkills] = divideArray(roleSkills, ({ id }) => !!getProfileSkill(id));

  const matchingSkillsWithStatus = compact(
    matchingSkills.map((skill) => {
      const profileSkill = getProfileSkill(skill.id);
      return { ...skill, top: !!profileSkill?.top, developmental: !!profileSkill?.developmental };
    })
  );

  const handleAddSkillClick = (skill: PureCustomValue) => {
    modal.setData({ skill });
    modal.setType("addSkill");
  };

  const matchScore = selectedRoleScores?.normalized_score;

  return (
    <Section
      title={t("title")}
      headerAdditionalContent={
        matchScore ? (
          <div className={css.matchContainer}>
            <RadialBar strokeWidth={2} size="md" value={matchScore * 100} />
            {t("match", {
              matchValue: getMatchDisplayValue(matchScore, { showPercentage: true })
            })}
          </div>
        ) : undefined
      }
    >
      <div className={css.mainContainer}>
        <div className={css.skillRow}>
          {matchingSkillsWithStatus.map((skill) => (
            <MarketplaceSkillBadge key={skill?.id} skill={skill} kind="matching" />
          ))}
        </div>
        <div className={css.skillRow}>
          {unmatchedSkills.map((skill) => (
            <div key={skill.id} className={css.unmatchedSkill}>
              <MarketplaceSkillBadge key={skill.id} skill={skill} kind="unmatched" />
              <ActionIcon
                classes={{ root: css.addSkillButton, icon: css.addSkillIcon }}
                onClick={() => handleAddSkillClick(skill)}
                name="add"
                size="sm"
                color="paletteWhite1"
              />
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};
